// Copyright: IQGeo Limited 2010-2019
import L from 'leaflet';
import myw from 'myWorld/base/core';

myw.Layer = L.Layer.extend({
    /**
     * @class A layer to display data from a datasource on a map<br/>
     * Composed of a leaflet layer instance (an {@link L.ILayer}) and additional behaviour such as:
     * name, validity, enabled/disabled, visibility, datasource
     * @param  {Datasource}                 datasource
     * @param  {layerDefinition}                layerDef        Layer definition
     * @constructs
     * @extends {L.ILayer}
     */
    initialize(datasource, layerDef) {
        /** Datasource for this layer
         * @type {IDatasource}    */
        this.datasource = datasource;

        /** layer definition
         * @type {layerDefinition} */
        this.layerDef = layerDef;

        /** Whether this layer is visible on the map or not
         * @type {boolean}    */
        this.isVisible = false;

        /** Whether this layer has been correctly initialized or not
         * @type {boolean}    */
        //assume invalid until we're sure it's valid - datasources may take a long time to initialize
        //and the layer should be unavailable until it does
        this.isInvalid = true;

        /** Promise to resolve when the layer is successfuly initialized
         * @type {promise} */
        this.initialized = datasource.initialized
            .then(() => datasource.createLayer(layerDef))
            .then(leafletLayer => {
                this.leafletLayer = leafletLayer;
                this.isInvalid = !leafletLayer;
                return leafletLayer;
            })
            .then(leafletLayer => {
                this._setupEventHandlers();
                return leafletLayer;
            })
            .catch(reason => {
                this.isInvalid = true;
                console.log(`Error instantiating layer '${layerDef.name}'. Reason: `, reason);
            });
    },

    _setupEventHandlers(leafletLayer) {
        //handlers for when state comming from leaflet layer
        if (leafletLayer && typeof leafletLayer.on == 'function') {
            leafletLayer.on('rendering-started', this._renderStarted, this);
            leafletLayer.on('rendering-ended', this._renderEnded, this);
        }
    },

    /*
     * Informs the application that rendering for self has started
     */
    _renderStarted() {
        if (this._map && !this._rendering) {
            this._map.app.statusBusy('render_map', myw.msg('MapControl', 'drawing_map_status'));
            this._rendering = this._map;
            this.datasource.system.recordDataAccess(
                this.datasource.database.applicationName,
                `layer.${this.getName()}`
            );
        }
    },

    /*
     * Informs the application that rendering for self has finished
     */
    _renderEnded() {
        if (this._rendering) {
            this._rendering.app.statusDone('render_map');
            this._rendering = false;
        }
    },

    /**
     * Sets the actual visibility of the layer by adding or removing it from the map
     * @param {boolean} visible
     * @private
     */
    _setVisibility(visible) {
        if (!this.leafletLayer) {
            this.isVisible = false;
            return;
        }

        this.isVisible = visible;

        if (visible) {
            this.map.addLayer(this.leafletLayer);
        } else {
            this.map.removeLayer(this.leafletLayer);
        }
    },

    /**
     * Whether the layer is visible on the map at the given zoom level, based on it's config
     * @param {integer} z Zoom level
     * @returns {boolean}
     */
    isVisibleAtZoom(z) {
        return z >= this.layerDef.min_scale && z <= this.layerDef.max_scale;
    },

    /**
     * Returns the one character that identifies an overlay layer
     * @returns {character}
     */
    getCode() {
        return this.layerDef.code;
    },

    /**
     * returns the name of this layer.
     * @returns {string}
     */
    getName() {
        return this.layerDef.name;
    },

    /**
     * returns the description of this layer.
     * @returns {string}
     */
    getDescription() {
        const desc = this.layerDef.description;
        return desc ? desc : '';
    },
    /**
     * returns the image filename of the thumbnail representing this layer.
     * @returns {string}
     */
    getThumbnail() {
        return this.layerDef.thumbnail;
    },

    onAdd(map) {
        this._map = map;
        this.onAddPromise = this.initialized.then(() => {
            //layer may have been removed from map while waiting for initialization
            if (map == this._map && this.leafletLayer) {
                map.addLayer(this.leafletLayer);
            }
        });
    },

    onRemove(map) {
        this._map = null;
        this.initialized.then(() => {
            map.removeLayer(this.leafletLayer);
        });
    },

    /**
     * Refreshes the layer visualization on the map
     */
    update() {
        this.redraw();
    },

    /**
     * Refreshes the layer visualization on the map
     * @param  {boolean}         hard    Redraws by removing and readding the layer to the map
     */
    redraw(hard) {
        if (!this.leafletLayer) return;

        if (hard || typeof this.leafletLayer.redraw != 'function') {
            this._setVisibility(false);
            this._setVisibility(true);
        } else {
            this.leafletLayer.redraw();
        }
    },

    /**
     * Called when a feature on layer has been modified. Refreshes the layer.
     * @param  {string}         type    Feature type
     * @param  {Feature}    feature
     */
    featureModified(type, feature) {
        if (!this.isVisible) return;

        // featureModified is not part of Leaflet Layer interface so no guarantees
        // it will be defined on arbitary layer so check it is defined.
        if (feature && this.leafletLayer.featureModified) {
            this.leafletLayer.featureModified(type, feature);
        } else {
            //redraw whole layer
            this.update();
        }
    },

    /**
     * Returns the mode in which this layer is being displayed: 'local' or 'master'
     * Only applies to native app. Returns undefined if not in native app
     * @return {string|undefined}
     */
    appViewMode() {
        const ds = this.datasource;

        if (myw.isNativeApp) {
            const mode = ds.supportsReplication ? ds.modeForLayer(this.getName()) : 'master';

            return mode;
        }
    }
});

export default myw.Layer;
